import type { OpencodeSession } from "schemas/generated/opencode";

import React from "react";

import SvgMessages from "@svg/messages";

import { useStreamingSessions } from "@agent-ui/features/chat/store/SessionStoreContext";
import { Button } from "@replo/design-system/components/button/Button";
import { Empty } from "@replo/design-system/components/empty/Empty";
import { ListItem } from "@replo/design-system/components/list-item/ListItem";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import { Spinner } from "@replo/design-system/components/spinner/Spinner";
import {
  TooltipContent,
  TooltipRoot,
  TooltipTrigger,
} from "@replo/design-system/components/tooltip/Tooltip";
import { useOverflowDetection } from "@replo/design-system/hooks/use-overflow-detection";
import { skipToken } from "@tanstack/react-query";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import {
  useLocalStorage,
  useLocalStorageState,
} from "@v2/hooks/useLocalStorage";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { opencodeRouter } from "@v2/trpc/routes/opencode";
import InfiniteScroll from "react-infinite-scroll-component";
import { useSearchParams } from "react-router-dom";
import { useOnValueChange } from "replo-utils/react/use-on-value-change";
import { z } from "zod";

const PAGE_SIZE = 50;

function SessionListItem({
  session,
  isSelected,
  isStreaming,
  onClick,
}: {
  session: Pick<OpencodeSession, "id" | "title">;
  isSelected: boolean;
  isStreaming: boolean;
  onClick: () => void;
}) {
  const [overflowRef, isOverflowing] = useOverflowDetection();
  const title = session.title ?? "New Chat";

  return (
    <TooltipRoot disabled={!isOverflowing} disableHoverablePopup>
      <TooltipTrigger
        render={
          <ListItem
            render={<button type="button" />}
            selected={isSelected}
            className="w-full"
            UNSAFE_className="hover:bg-surface"
            onClick={onClick}
            label={
              <span ref={overflowRef} className="block truncate">
                {title}
              </span>
            }
            endEnhancer={isStreaming ? <Spinner /> : null}
          />
        }
      />
      <TooltipContent>{title}</TooltipContent>
    </TooltipRoot>
  );
}

function SessionList({
  sessions,
  isLoading,
  sessionUuid,
  streamingSessions,
  hasNextPage,
  onNewChat,
  onSessionClick,
  onFetchNextPage,
}: {
  sessions: Pick<OpencodeSession, "id" | "title">[];
  isLoading: boolean;
  sessionUuid: string | null;
  streamingSessions: string[];
  hasNextPage: boolean;
  onNewChat: () => void;
  onSessionClick: (sessionId: string) => void;
  onFetchNextPage: () => void;
}) {
  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: 16 }).map((_, index) => (
          <Skeleton key={index} className="h-8 w-full" />
        ))}
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className="h-full w-full flex items-center justify-center p-4">
        <Empty
          media={<SvgMessages className="h-16 w-auto" />}
          title="No chats yet"
          description="Chat history will show up here."
        >
          <Button variant="secondary" size="base" onClick={onNewChat}>
            Create new chat
          </Button>
        </Empty>
      </div>
    );
  }

  return (
    <InfiniteScroll
      dataLength={sessions.length}
      next={onFetchNextPage}
      hasMore={hasNextPage}
      scrollableTarget="chats-scroll-container"
      loader={Array.from({ length: 3 }).map((_, index) => (
        <Skeleton key={index} className="h-8 w-full" />
      ))}
    >
      <div className="flex flex-col gap-2">
        {sessions.map((session) => {
          // TODO(MC): should call the other hook for the single sessionId
          const isStreaming = streamingSessions.includes(session.id);

          return (
            <SessionListItem
              key={session.id}
              session={session}
              isSelected={sessionUuid === session.id}
              isStreaming={isStreaming}
              onClick={() => onSessionClick(session.id)}
            />
          );
        })}
      </div>
    </InfiniteScroll>
  );
}

function ManageChats() {
  const logEvent = useLogAnalytics();
  const projectId = useCurrentProjectId();
  const { user } = useCurrentUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionUuid = searchParams.get("sessionUuid");
  const storage = useLocalStorage();
  const streamingSessions = useStreamingSessions();
  const [, setIsChatSidebarOpen] = useLocalStorageState(
    "replo.editor.chatSidebarOpen",
    false,
    { schema: z.boolean() },
  );

  const {
    data: sessionsData,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isError,
  } = opencodeRouter.getPaginatedSessions.useInfiniteQuery(
    projectId
      ? {
          projectId,
          limit: PAGE_SIZE,
        }
      : skipToken,
    {
      getNextPageParam: ({ nextCursor }) => nextCursor,
      staleTime: 0,
    },
  );

  const sessions = React.useMemo(() => {
    const fetched = sessionsData?.pages?.flatMap((page) => page.sessions) ?? [];
    // NOTE (Max, 2026-02-26): Show optimistic entries for sessions that are
    // streaming but not yet persisted in the DB. The frontend sets streaming
    // state immediately, but the Durable Object persists the session async.
    const fetchedIds = new Set(fetched.map((s) => s.id));
    const optimistic = streamingSessions
      .filter((id) => !fetchedIds.has(id))
      .map((id) => ({ id, title: null as string | null }));
    return [...optimistic, ...fetched];
  }, [sessionsData, streamingSessions]);

  useOnValueChange(sessionsData, (oldValue, newValue) => {
    const stored = storage.getItem("replo.editor.chatSidebarOpen");
    const prevCount = oldValue?.pages?.[0]?.sessions?.length ?? 0;
    const nextCount = newValue?.pages?.[0]?.sessions?.length ?? 0;
    if (stored != null) {
      return;
    }

    if (prevCount === 0 && nextCount > 0) {
      setIsChatSidebarOpen(true);
    }
  });

  const handleNewChat = () => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("sessionUuid", "");
      return newParams;
    });
    setIsChatSidebarOpen(false);
    logEvent("chat.new");
  };

  const handleSessionClick = (sessionId: string) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("sessionUuid", sessionId);
      return newParams;
    });
  };

  if (!projectId || !user?.id) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner className="size-6" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center h-full">
        Error Loading Chats
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 p-3 h-full min-h-0 w-full bg-sheet relative">
      <h2 className="header-h6 text-default">All Chats</h2>

      {sessions.length > 0 && (
        <Button
          variant="secondary"
          size="base"
          className="w-full"
          onClick={handleNewChat}
        >
          New Chat
        </Button>
      )}

      <div
        id="chats-scroll-container"
        className="flex-1 overflow-y-auto no-scrollbar"
      >
        <SessionList
          sessions={sessions}
          isLoading={isLoading}
          sessionUuid={sessionUuid}
          streamingSessions={streamingSessions}
          hasNextPage={hasNextPage ?? false}
          onNewChat={handleNewChat}
          onSessionClick={handleSessionClick}
          onFetchNextPage={() => void fetchNextPage()}
        />
      </div>
    </div>
  );
}

export default ManageChats;
